import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-steel">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-steel/30 border-t-steel" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

// `nav` ([{id, label}]) + `onNavigate(id)` add a slim "jump to section" row
// under the main bar. It lives inside the sticky header so it stays reachable
// from anywhere on a long page without any offset maths -- and stays quiet:
// small, muted text that only brightens on hover.
export function Header({ user, editMode, onToggleEdit, nav, onNavigate }) {
  return (
    <header className="sticky top-0 z-20 w-full bg-charcoal text-white shadow-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-3 sm:gap-3 sm:px-4">
        <Link to="/" className="flex min-w-0 shrink items-center gap-2 sm:gap-3">
          <img src="/logo.png" alt="Ascoat" className="h-6 w-auto shrink-0" />
          <span className="hidden text-sm font-medium text-mist/70 sm:inline">Homepage</span>
        </Link>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {onToggleEdit && (
            <button
              onClick={onToggleEdit}
              className={`hidden rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition md:inline-flex ${
                editMode ? 'bg-amber text-charcoal' : 'border border-white/20 hover:bg-white/10'
              }`}
            >
              {editMode ? 'Done editing' : 'Edit layout'}
            </button>
          )}
          {user?.role === 'manager' && (
            <Link to="/manage" className="rounded-md border border-white/20 px-2 py-1.5 text-xs font-bold uppercase tracking-wide hover:bg-white/10 sm:px-3">
              Manage
            </Link>
          )}
          {user && (
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium">{user.name}</div>
            </div>
          )}
          {user && (
            <a href="/auth/logout" className="rounded-md border border-white/20 px-2 py-1.5 text-xs hover:bg-white/10">
              Sign out
            </a>
          )}
        </div>
      </div>
      {nav && nav.length > 0 && (
        <nav aria-label="Jump to section" className="border-t border-white/10">
          <div className="mx-auto flex max-w-6xl items-center gap-4 overflow-x-auto px-3 py-1.5 [scrollbar-width:none] sm:px-4 [&::-webkit-scrollbar]:hidden">
            {nav.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                className="shrink-0 whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-mist/50 transition hover:text-white focus-visible:text-white"
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

export function Page({ children, wide }) {
  return <main className={`mx-auto px-4 py-5 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>{children}</main>;
}

// Deterministic colour for the fallback monogram, so the same app always gets the same colour.
function hashHue(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

export function AppIcon({ app, size = 40 }) {
  const [error, setError] = useState(false);
  const src = app.iconPath || (app.hasPocketidLogo && app.pocketidClientId ? `/api/apps/${app.pocketidClientId}/icon` : null);

  if (src && !error) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className="rounded-lg object-contain bg-white/60 dark:bg-white/10"
        style={{ width: size, height: size }}
        onError={() => setError(true)}
      />
    );
  }

  const seed = app.name || app.key || '?';
  const letter = seed.trim().charAt(0).toUpperCase() || '?';
  const hue = hashHue(seed);
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-lg font-bold text-white"
      style={{ width: size, height: size, background: `hsl(${hue} 50% 40%)`, fontSize: size * 0.42 }}
    >
      {letter}
    </div>
  );
}

export function fmtDay(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' });
}

export function fmtMoney(n) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n || 0);
}

export function ErrorNote({ children }) {
  return <p className="rounded-lg bg-ascred/10 px-3 py-2 text-xs text-ascred">{children}</p>;
}

const TASK_STATUS_CLASSES = {
  'Not Started': 'bg-steel/20 text-steel',
  Started: 'bg-[#3a5f8a] text-white',
  Completed: 'bg-[#4f7a4a] text-white',
  Canceled: 'bg-steel/20 text-steel line-through',
  Deferred: 'bg-[#8a6a3f] text-white',
};

export function TaskStatusBadge({ status }) {
  return <span className={`badge ${TASK_STATUS_CLASSES[status] || 'bg-steel/20 text-steel'}`}>{status || '—'}</span>;
}

const TASK_PRIORITY_CLASSES = {
  Low: 'bg-steel/20 text-steel',
  Normal: 'bg-steel/20 text-steel',
  High: 'bg-amber/30 text-charcoal dark:text-amber',
  Urgent: 'bg-ascred/20 text-ascred',
};

export function TaskPriorityBadge({ priority }) {
  return <span className={`badge ${TASK_PRIORITY_CLASSES[priority] || 'bg-steel/20 text-steel'}`}>{priority || '—'}</span>;
}

// A collapsible homepage section: owns the single title + chevron-toggle
// header for whatever it wraps. Lazy-mounts its children the first time it's
// opened, then just CSS-hides on re-collapse -- a section that defaults
// collapsed never fires its widgets' API calls (or, for Analytics, loads its
// iframe + external embed script) until someone actually opens it, while a
// section that's already been opened doesn't refetch/flicker on re-toggle.
export function CollapsibleSection({ id, title, open, onToggle, children, className = 'mt-8' }) {
  const [everOpened, setEverOpened] = useState(open);
  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  return (
    // scroll-mt clears the sticky header + jump row when scrolled to via an id.
    <div id={id} className={`${className} scroll-mt-24`}>
      <button type="button" onClick={onToggle} className="mb-3 flex w-full items-center gap-2 text-left">
        <span className={`text-steel transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>
        <span className="text-sm font-bold uppercase tracking-wide text-steel">{title}</span>
      </button>
      {everOpened && <div className={open ? '' : 'hidden'}>{children}</div>}
    </div>
  );
}
